<template>
  <div
    ref="widgetRef"
    class="relative"
  >
    <!-- Notification bell -->
    <button
      id="notification-bell"
      class="relative bg-white rounded-[55px] shadow-[0_4px_5.25px_rgba(0,0,0,0.02)] w-9 h-9 md:w-12 md:h-12 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
      aria-label="Notifications"
      @click="toggleDropdown"
    >
      <svg
        width="17"
        height="20"
        viewBox="0 0 17 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="w-[14px] h-[16px] md:w-[17px] md:h-[20px] shrink-0"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M1.87558 7.07667C1.87558 3.66281 4.64922 0.889168 8.06308 0.889168C11.4678 0.889168 14.2506 3.67197 14.2506 7.07667V9.72583C14.2506 9.92279 14.2952 10.2111 14.3796 10.5191C14.4631 10.8234 14.5709 11.0939 14.669 11.2656L15.7201 13.0113C16.5502 14.3948 15.8866 16.197 14.3574 16.7032C10.2655 18.0701 5.85044 18.07 1.75855 16.7029L1.756 16.702C0.983793 16.4407 0.404683 15.9003 0.145753 15.201C-0.113477 14.5009 -0.0252919 13.7129 0.397326 13.0106L1.45005 11.2621C1.45028 11.2617 1.45052 11.2613 1.45075 11.261C1.55113 11.0922 1.66108 10.8227 1.74594 10.517C1.83105 10.2104 1.87558 9.92258 1.87558 9.72583V7.07667ZM8.06308 2.26417C5.40861 2.26417 3.25058 4.4222 3.25058 7.07667V9.72583C3.25058 10.0883 3.17594 10.5062 3.07084 10.8848C2.96568 11.2636 2.8147 11.6582 2.6311 11.9662L2.62956 11.9688L1.5755 13.7195C1.57546 13.7195 1.57553 13.7194 1.5755 13.7195C1.34738 14.0987 1.33465 14.452 1.43519 14.7236C1.53596 14.9957 1.77689 15.2571 2.1956 15.3992C6.00466 16.6715 10.1137 16.6714 13.9227 15.3988L13.9248 15.3981C14.613 15.1706 14.9206 14.352 14.5413 13.7192C14.5412 13.719 14.5414 13.7194 14.5413 13.7192L13.4808 11.9578C13.3047 11.6526 13.1572 11.2607 13.0536 10.8828C12.9502 10.5056 12.8756 10.088 12.8756 9.72583V7.07667C12.8756 4.43136 10.7084 2.26417 8.06308 2.26417Z"
          fill="#1C252E"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M5.72715 1.59165C6.09331 0.657309 7.00237 0 8.06308 0C9.1238 0 10.0329 0.657309 10.399 1.59165C10.4946 1.83555 10.4433 2.11257 10.2666 2.30604C10.09 2.4995 9.8188 2.57578 9.56723 2.50274C9.31294 2.42891 9.0563 2.3732 8.79619 2.34167L8.79364 2.34136C8.00126 2.24231 7.25256 2.3011 6.55916 2.50267C6.30757 2.57581 6.03628 2.49959 5.8596 2.30612C5.68291 2.11266 5.63155 1.83559 5.72715 1.59165Z"
          fill="#1C252E"
        />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.00058 16.3808C6.00058 16.9423 6.23415 17.4663 6.60588 17.838C6.97761 18.2098 7.5016 18.4433 8.06308 18.4433C9.19588 18.4433 10.1256 17.5136 10.1256 16.3808H11.5006C11.5006 18.273 9.95528 19.8183 8.06308 19.8183C7.12122 19.8183 6.25188 19.4286 5.63361 18.8103C5.01534 18.192 4.62558 17.3227 4.62558 16.3808H6.00058Z"
          fill="#1C252E"
        />
      </svg>

      <!-- Notification badge -->
      <transition
        enter-active-class="transition transform duration-300 ease-out"
        enter-from-class="scale-0 opacity-0"
        enter-to-class="scale-100 opacity-100"
        leave-active-class="transition transform duration-200 ease-in"
        leave-from-class="scale-100 opacity-100"
        leave-to-class="scale-0 opacity-0"
      >
        <span
          v-if="unreadCount > 0"
          :key="unreadCount"
          class="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center shadow-sm animate-pulse-once"
        >
          {{ displayUnreadCount }}
        </span>
      </transition>
    </button>

    <!-- Dropdown -->
    <div
      v-if="isOpen"
      class="fixed sm:absolute left-1/2 sm:left-auto right-auto sm:right-0 top-[76px] sm:top-full -translate-x-1/2 sm:translate-x-0 mt-0 sm:mt-2 w-[calc(100vw-32px)] sm:w-[360px] max-w-[360px] bg-white rounded-[15px] shadow-[0_24px_50px_-12px_rgba(0,0,0,0.2)] border border-gray-100 flex flex-col z-[100]"
      style="max-height: calc(100vh - 100px);"
    >
      <div class="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
        <h3 class="font-['Raleway',sans-serif] font-bold text-[16px] text-[#043f42] m-0 leading-none">
          Notifications
        </h3>
        <div class="flex items-center gap-3">
          <button
            v-if="unreadCount > 0"
            class="text-[12px] font-medium text-[#02949b] hover:text-[#01d680] transition-colors bg-transparent border-none p-0 cursor-pointer"
            @click="handleMarkAllRead"
          >
            Mark as read
          </button>
          <button
            v-if="notifications.length > 0"
            class="text-[12px] font-medium text-gray-400 hover:text-red-500 transition-colors bg-transparent border-none p-0 cursor-pointer"
            @click="handleClearAll"
          >
            Clear all
          </button>
        </div>
      </div>

      <div class="flex-1 max-h-[400px] overflow-y-auto">
        <div
          v-if="notifications.length === 0"
          class="p-8 text-center"
        >
          <p class="text-gray-500 text-[14px]">
            You have no notifications yet.
          </p>
        </div>
        <template v-else>
          <NotificationRow
            v-for="notification in notifications"
            :key="notification.id"
            :notification="notification"
            :compact="true"
            @click="closeDropdown"
          />
        </template>
      </div>

      <div class="border-t border-gray-100 p-2 shrink-0">
        <a
          href="/notifications"
          class="block w-full py-2.5 text-center text-[14px] font-medium text-[#043f42] hover:bg-gray-50 rounded-[10px] transition-colors"
        >
          View all notifications
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useNotifications } from '../../composables/useNotifications'
import NotificationRow from './NotificationRow.vue'

const { notifications, unreadCount, fetchRecent, markAllRead, clearAll, startPolling, stopPolling } = useNotifications()

const isOpen = ref(false)
const widgetRef = ref(null)

const displayUnreadCount = computed(() => {
  return unreadCount.value > 99 ? '99+' : unreadCount.value
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    fetchRecent()
  }
}

const closeDropdown = () => {
  isOpen.value = false
}

const handleMarkAllRead = () => {
  markAllRead()
}

const handleClearAll = () => {
  clearAll()
}

// Click outside to close
const handleClickOutside = (event) => {
  if (isOpen.value && widgetRef.value && !widgetRef.value.contains(event.target)) {
    closeDropdown()
  }
}

// Escape to close
const handleKeydown = (event) => {
  if (isOpen.value && event.key === 'Escape') {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)
  // AC1: polling interval fires every 30s
  startPolling(30000)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
  stopPolling()
})
</script>

<style scoped>
.animate-pulse-once {
  animation: pulse-once 0.5s ease-out;
}
@keyframes pulse-once {
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}
</style>
